import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useSubsetExportData } from '../../../api/hooks/useSubsetExportData/useSubsetExportData';
import { isContentTooLargeError } from '../../../api/utils/isContentTooLargeError';
import { abortActivePolls } from '../../../api/utils/response/pollAbortRegistry';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { fetchMissingRowData } from '../../../hooks/useControlAction/utils/fetchMissingRowData';
import { Dataset, GridRow, JSONObject } from '../../../types/types';
import { getDataKey } from '../../../utils/getDataKey';
import {
  registerIdleHold,
  unregisterIdleHold,
} from '../../../utils/idleHoldRegistry';
import { DATASET_EXPORT_FILE_PREFIX } from '../constants';
import { canExportLoadedRows } from '../utils/canExportLoadedRows';
import { canExportSubsetsOnly } from '../utils/canExportSubsetsOnly';
import { downloadExport } from '../utils/downloadExport';
import { terminateExportWorker } from '../utils/exportWorkerInstance';
import findJsonReservedColumn from '../utils/json/findJsonReservedColumn';
import { hasExportableSubsetSections } from '../utils/hasExportableSubsetSections';
import { loadSubsetRowsByMaster } from '../utils/loadSubsetRowsByMaster';
import { resolveDelimiter } from '../utils/resolveDelimiter';
import { resolveExportColumns } from '../utils/resolveExportColumns';
import { resolveExportRequestItemNames } from '../utils/resolveExportRequestItemNames';
import { resolveSubsetFilters } from '../utils/resolveSubsetFilters';
import { resolveSubsetSorting } from '../utils/resolveSubsetSorting';
import {
  DecimalSeparatorMode,
  DelimiterMode,
  ExportEncoding,
  ExportFormat,
  ExportLazyState,
  ExportOptions,
  ExportPayloadChildSection,
  ExportPayloadSection,
  ExportProgressState,
  ExportSection,
  JsonValueMode,
  SubsetExportState,
} from '../types';

type UseExportDownloadParams = {
  format: ExportFormat;
  encoding: ExportEncoding;
  delimiterMode: DelimiterMode;
  customDelimiter: string;
  decimalSeparator: DecimalSeparatorMode;
  jsonValueMode: JsonValueMode;
  includeColumnNames: boolean;
  useDisplayFormatting: boolean;
  header: string;
  footer: string;
  isWithSubsetsMode: boolean;
  exportDataset: Dataset | undefined;
  dataset: Dataset | undefined;
  rows: GridRow[];
  // Server total for the current filters; undefined means completeness is unknown.
  totalCount: number | undefined;
  // Grid's server-side query snapshot; undefined when the API sends no cached query.
  cachedQueryId: string | undefined;
  sections: ExportSection[];
  masterSection: ExportSection | undefined;
  hasExportableColumns: boolean;
  subsetExportState: SubsetExportState | undefined;
  activeFilters: Record<string, unknown> | null;
  // Dataset control data of the master dataset (main export only).
  datasetControl: JSONObject | null;
  activeSorting: { field: string; order: number }[];
  slaveDatasets: Record<string, Dataset>;
  slaveLazyStates: Record<string, unknown>;
  rowsSelectedForExport: GridRow[];
  virtualRowsSelectedForExport: number;
  isSelectAllCheckedForExport: boolean;
  deselectedRowIdsForExport: Set<string>;
  useAllRows: boolean;
};

export type UseExportDownloadResult = {
  isExporting: boolean;
  exportProgress: ExportProgressState | null;
  handleDownload: () => Promise<void>;
  handleCancelDownload: () => void;
};

export const useExportDownload = ({
  format,
  encoding,
  delimiterMode,
  customDelimiter,
  decimalSeparator,
  jsonValueMode,
  includeColumnNames,
  useDisplayFormatting,
  header,
  footer,
  isWithSubsetsMode,
  exportDataset,
  dataset,
  rows,
  totalCount,
  cachedQueryId,
  sections,
  masterSection,
  hasExportableColumns,
  subsetExportState,
  activeFilters,
  datasetControl,
  activeSorting,
  slaveDatasets,
  slaveLazyStates,
  rowsSelectedForExport,
  virtualRowsSelectedForExport,
  isSelectAllCheckedForExport,
  deselectedRowIdsForExport,
  useAllRows,
}: UseExportDownloadParams): UseExportDownloadResult => {
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportProgress, setExportProgress] =
    useState<ExportProgressState | null>(null);
  // Ref-based controller persists across renders without triggering re-renders.
  const exportAbortRef = useRef<AbortController | null>(null);

  const { loadDatasetDefinition, loadDatasetRows, loadSubsetRows } =
    useSubsetExportData();
  const { t } = useTranslate();

  const needsMasterIdItem = useMemo<boolean>(
    () => isWithSubsetsMode && hasExportableSubsetSections(sections),
    [isWithSubsetsMode, sections]
  );

  // Only an id-less result set needs the snapshot: with no unique key, tie order is
  // otherwise arbitrary between two queries.
  const lockedCachedQueryId: string | undefined =
    exportDataset && !exportDataset.idItem ? cachedQueryId : undefined;

  const loadAllMasterRows = async (
    itemNames: string[],
    signal?: AbortSignal
  ): Promise<GridRow[]> => {
    if (!exportDataset) {
      return [];
    }

    if (subsetExportState && dataset) {
      return loadSubsetRows({
        masterDataset: dataset,
        masterRows: [
          subsetExportState.parentRow ?? {
            [subsetExportState.parentIdItem]: subsetExportState.parentId,
          },
        ],
        dataset: exportDataset,
        activeSorting: subsetExportState.activeSorting ?? [],
        activeFilters: subsetExportState.activeFilters ?? null,
        includeLimit: false,
        itemNames,
        // The id is only requested when child rows will be grouped under master rows.
        includeIdItem: needsMasterIdItem,
        signal,
      });
    }

    // A fresh un-paginated query can reorder rows that have no unique key; the loaded
    // grid rows are the whole result set in the order the user sees.
    if (
      !exportDataset.idItem &&
      canExportLoadedRows(rows, totalCount, itemNames)
    ) {
      return rows;
    }

    return loadDatasetRows({
      dataset: exportDataset,
      activeSorting: activeSorting as { field: string; order: number }[],
      activeFilters,
      datasetControl: datasetControl ?? undefined,
      includeLimit: false,
      itemNames,
      includeIdItem: needsMasterIdItem,
      lockedCachedQueryId,
      signal,
    });
  };

  const resolveMasterRowsForExport = async (
    itemNames: string[],
    signal?: AbortSignal
  ): Promise<GridRow[]> => {
    if (useAllRows || isSelectAllCheckedForExport) {
      return loadAllMasterRows(itemNames, signal);
    }

    if (!exportDataset) {
      return [];
    }

    if (virtualRowsSelectedForExport > 0) {
      const allRows = await loadAllMasterRows(itemNames, signal);
      signal?.throwIfAborted();

      // Reuse the same virtual-row expansion algorithm as bulk form/actions.
      return (await fetchMissingRowData(
        exportDataset,
        rowsSelectedForExport,
        async (): Promise<any[]> => allRows,
        deselectedRowIdsForExport
      )) as GridRow[];
    }

    // No server id to filter by; the checked rows already carry every grid-loaded column.
    if (!exportDataset.idItem) {
      return rowsSelectedForExport;
    }

    if (subsetExportState && dataset) {
      return loadSubsetRows({
        masterDataset: dataset,
        masterRows: [
          subsetExportState.parentRow ?? {
            [subsetExportState.parentIdItem]: subsetExportState.parentId,
          },
        ],
        dataset: exportDataset,
        activeSorting: subsetExportState.activeSorting ?? [],
        activeFilters: subsetExportState.activeFilters ?? null,
        selectedRows: rowsSelectedForExport,
        includeLimit: false,
        itemNames,
        includeIdItem: needsMasterIdItem,
        signal,
      });
    }

    return loadDatasetRows({
      dataset: exportDataset,
      activeSorting: activeSorting as { field: string; order: number }[],
      activeFilters,
      datasetControl: datasetControl ?? undefined,
      selectedRows: rowsSelectedForExport,
      includeLimit: false,
      itemNames,
      includeIdItem: needsMasterIdItem,
      lockedCachedQueryId,
      signal,
    });
  };

  const handleCancelDownload = useCallback((): void => {
    if (!exportAbortRef.current) {
      return;
    }

    exportAbortRef.current.abort();
    unregisterIdleHold(exportAbortRef.current);
    exportAbortRef.current = null;
    // CapacitorHttp can't abort the in-flight request, but this stops any further polls.
    abortActivePolls();
    setIsExporting(false);
    setExportProgress(null);
    toast(t('export.cancelled'));
  }, [t]);

  const handleDownload = async (): Promise<void> => {
    if (!exportDataset?.datasetId || !hasExportableColumns) {
      toast(t('export.noExportableData'));
      return;
    }

    const masterColumns =
      masterSection?.columns.filter((column) => column.selected) ?? [];

    if (
      !masterSection ||
      (masterColumns.length === 0 &&
        !canExportSubsetsOnly(isWithSubsetsMode, exportDataset, sections))
    ) {
      toast(t('export.noExportableData'));
      return;
    }

    const reservedColumn = findJsonReservedColumn(
      sections,
      masterSection,
      format,
      isWithSubsetsMode
    );

    if (reservedColumn) {
      toast(
        t('export.jsonReservedColumn').replace('{column}', reservedColumn.label)
      );
      return;
    }

    const abortController = new AbortController();
    exportAbortRef.current = abortController;

    let fetchProcessed = 0;
    let fetchTotal = 1;
    const reportFetchProgress = (): void => {
      setExportProgress({
        processed: fetchProcessed,
        total: fetchTotal,
        phase: 'fetching',
      });
    };

    try {
      // Pauses the auto-logout countdown until the download settles
      registerIdleHold(abortController);
      setIsExporting(true);
      reportFetchProgress();

      const masterRows = await resolveMasterRowsForExport(
        resolveExportRequestItemNames(masterColumns),
        abortController.signal
      );

      abortController.signal.throwIfAborted();

      if (masterRows.length === 0) {
        toast(t('export.noExportRows'));
        return;
      }

      fetchProcessed = 1;
      const enabledSubsetSections = isWithSubsetsMode
        ? sections.filter((item) => item.kind === 'subset' && item.enabled)
        : [];
      fetchTotal = 1 + enabledSubsetSections.length * masterRows.length;
      reportFetchProgress();

      const childSections: ExportPayloadChildSection[] = [];

      for (const section of enabledSubsetSections) {
        abortController.signal.throwIfAborted();
        let subsetDataset = slaveDatasets[section.id];
        if (!subsetDataset?.items) {
          const loadedSubsetDataset = await loadDatasetDefinition({
            subsetDatasetId: section.id,
            cachedSubsetDataset: subsetDataset,
            signal: abortController.signal,
          });

          if (!loadedSubsetDataset) {
            fetchProcessed += masterRows.length;
            reportFetchProgress();
            continue;
          }

          subsetDataset = loadedSubsetDataset;
        }

        const sourceColumns =
          section.columns.length > 0
            ? section.columns
            : resolveExportColumns(
                subsetDataset.items,
                true,
                subsetDataset.idItem
              );
        const columns = sourceColumns.filter((column) => column.selected);

        if (columns.length === 0) {
          fetchProcessed += masterRows.length;
          reportFetchProgress();
          continue;
        }

        const lazyState = slaveLazyStates[section.id] as
          | ExportLazyState
          | undefined;

        childSections.push({
          id: section.id,
          label: section.label,
          columns,
          rowsByMasterId: await loadSubsetRowsByMaster({
            masterDataset: dataset ?? null,
            subsetDataset,
            masterRows,
            itemNames: resolveExportRequestItemNames(columns),
            activeSorting: resolveSubsetSorting(lazyState, subsetDataset),
            activeFilters: resolveSubsetFilters(lazyState, subsetDataset),
            loadSubsetRows,
            onMasterRowProcessed: () => {
              fetchProcessed += 1;
              reportFetchProgress();
            },
            signal: abortController.signal,
          }),
        });
      }

      if (masterColumns.length === 0 && childSections.length === 0) {
        toast(t('export.noExportableData'));
        return;
      }

      const sectionsToExport: ExportPayloadSection[] = [
        {
          id: masterSection.id,
          label: masterSection.label,
          columns: masterColumns,
          rows: masterRows,
          ...(isWithSubsetsMode
            ? {
                // The serializer nests selected subsets under each master row by idItem.
                childSections,
                masterIdItem: getDataKey(exportDataset),
              }
            : {}),
        },
      ];

      const options: ExportOptions = {
        format,
        encoding,
        includeColumnNames,
        delimiter: resolveDelimiter(delimiterMode, customDelimiter),
        decimalSeparator,
        jsonValueMode,
        useDisplayFormatting,
        header,
        footer,
      };

      await downloadExport(
        `${DATASET_EXPORT_FILE_PREFIX}-${exportDataset.datasetId}`,
        sectionsToExport,
        options,
        (processed, total) =>
          setExportProgress({ processed, total, phase: 'generating' }),
        abortController.signal
      );
    } catch (error) {
      // AbortError is the user-cancelled path; handleCancelDownload already toasted.
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        if (isContentTooLargeError(error)) {
          toast(t('toasts.contentTooLargeError'));
          return;
        }
        // Rethrowing would be an unhandled rejection, leaving the button looking dead.
        console.error('Export failed', error);
        toast(t('export.failed'));
      }
    } finally {
      // Released unconditionally: a newer session reassigning the ref must not leak this hold.
      unregisterIdleHold(abortController);
      // Skip when handleCancelDownload already cleared this session's ref and state.
      if (exportAbortRef.current === abortController) {
        exportAbortRef.current = null;
        setIsExporting(false);
        setExportProgress(null);
      }
    }
  };

  // Keeps unmount cleanup on the latest closure without re-running it on every t/language change.
  const handleCancelDownloadRef = useRef(handleCancelDownload);
  handleCancelDownloadRef.current = handleCancelDownload;

  useEffect(
    () => () => {
      // Route through the same immediate cancel-and-toast path as the Cancel button.
      handleCancelDownloadRef.current();
      // Drop the worker on unmount so a future visit re-instantiates a fresh one.
      terminateExportWorker();
    },
    []
  );

  return {
    isExporting,
    exportProgress,
    handleDownload,
    handleCancelDownload,
  };
};
